import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  PromoSurveyInterestKey,
  PromoSurvey,
  PromoSurveyResponse,
} from '@kawai/shared';
import {
  fetchSurveys,
  fetchSurveysByInterestKey,
  fetchSurveyResponsesBySurvey,
} from '../repositories/surveys.js';

/**
 * M-78 (promo): 自テナントの全アンケート定義を取得する hook（配信作成画面のリンク挿入用）。
 *
 * cache key: `['promo', 'surveys', tenantId]`（interestKey 別 key の親前方一致で invalidate 可能）
 */
export function useSurveys(
  tenantId: string | null | undefined,
): UseQueryResult<PromoSurvey[], Error> {
  return useQuery({
    queryKey: ['promo', 'surveys', tenantId],
    queryFn: () => fetchSurveys(tenantId as string),
    enabled: !!tenantId,
    staleTime: 60 * 1000,
  });
}

/**
 * M-78 (promo): ある業態のアンケート定義一覧を取得する hook（販促プラン画面用）。
 *
 * cache key: `['promo', 'surveys', tenantId, interestKey]`
 * - 保存後は `queryClient.invalidateQueries({ queryKey: ['promo', 'surveys'] })` で再取得
 */
export function useSurveysByInterestKey(
  tenantId: string | null | undefined,
  interestKey: PromoSurveyInterestKey,
): UseQueryResult<PromoSurvey[], Error> {
  return useQuery({
    queryKey: ['promo', 'surveys', tenantId, interestKey],
    queryFn: () => fetchSurveysByInterestKey(tenantId as string, interestKey),
    enabled: !!tenantId,
    staleTime: 60 * 1000,
  });
}

/**
 * M-78 (promo): あるアンケートの回答一覧（最新状態 doc）を取得する hook（回答分布用）。
 *
 * cache key: `['promo', 'surveyResponses', tenantId, surveyId]`
 */
export function useSurveyResponses(
  tenantId: string | null | undefined,
  surveyId: string | null,
): UseQueryResult<PromoSurveyResponse[], Error> {
  return useQuery({
    queryKey: ['promo', 'surveyResponses', tenantId, surveyId],
    queryFn: () => fetchSurveyResponsesBySurvey(tenantId as string, surveyId as string),
    enabled: !!tenantId && !!surveyId,
    staleTime: 60 * 1000,
  });
}
